import type {
  Employee,
  EmployeeDetail,
  EmployeeFieldChange,
  EmployeeListResponse,
  PatchEmployeeBody,
  RevealEmployeeField,
  RevealEmployeeFieldResult,
} from "@evolua/api-client";

type PlatformState = {
  displayName: string | null;
  internalNotes: string | null;
  version: number;
  emailEditable: boolean;
  history: EmployeeFieldChange[];
};

const PLATFORM = new Map<string, PlatformState>();

function platformOf(employee: Employee): PlatformState {
  let state = PLATFORM.get(employee.id);
  if (!state) {
    state = {
      displayName: employee.fullName.split(" ")[0] ?? null,
      internalNotes: null,
      version: 1,
      emailEditable: false,
      history: [],
    };
    PLATFORM.set(employee.id, state);
  }
  return state;
}

const DEMO: Employee[] = [
  {
    id: "11111111-1111-4111-8111-111111111111",
    fullName: "Ana Souza",
    status: "ACTIVE",
    departmentName: "Operações",
    email: "ana.souza@demo.local",
    registrationNumber: "00001",
    positionName: "Analista",
    hireDate: "2021-03-15",
    syncedAt: "2026-08-11T12:00:00.000Z",
    dataOrigin: "ERP",
    cpf: "***.***.***-00",
    bankAccount: "****-00",
    salaryAmount: null,
  },
  {
    id: "22222222-2222-4222-8222-222222222222",
    fullName: "Bruno Lima",
    status: "ON_LEAVE",
    departmentName: "Financeiro",
    email: "bruno.lima@demo.local",
    registrationNumber: "00002",
    positionName: "Coordenador",
    hireDate: "2019-08-01",
    syncedAt: "2026-08-11T12:00:00.000Z",
    dataOrigin: "ERP",
    cpf: "***.***.***-00",
    bankAccount: "****-00",
    salaryAmount: null,
  },
  {
    id: "33333333-3333-4333-8333-333333333333",
    fullName: "Carla Mendes",
    status: "ACTIVE",
    departmentName: "RH",
    email: "carla.mendes@demo.local",
    registrationNumber: "00003",
    positionName: "Business Partner",
    hireDate: "2020-01-10",
    syncedAt: "2026-08-11T12:00:00.000Z",
    dataOrigin: "ERP",
    cpf: "***.***.***-00",
    bankAccount: "****-00",
    salaryAmount: null,
  },
  {
    id: "44444444-4444-4444-8444-444444444444",
    fullName: "Diego Alves",
    status: "ACTIVE",
    departmentName: "TI",
    email: "diego.alves@demo.local",
    registrationNumber: "00004",
    positionName: "Desenvolvedor",
    hireDate: "2022-11-20",
    syncedAt: null,
    dataOrigin: "PLATFORM",
    cpf: "***.***.***-00",
    bankAccount: "****-00",
    salaryAmount: null,
  },
];

const SECRETS: Record<
  string,
  { cpf: string; bankAccount: string; salaryAmount: number }
> = {
  "11111111-1111-4111-8111-111111111111": {
    cpf: "123.456.789-00",
    bankAccount: "1234-5 / 98765-1",
    salaryAmount: 5200,
  },
  "22222222-2222-4222-8222-222222222222": {
    cpf: "234.567.890-11",
    bankAccount: "1234-5 / 98765-2",
    salaryAmount: 7800,
  },
  "33333333-3333-4333-8333-333333333333": {
    cpf: "345.678.901-22",
    bankAccount: "1234-5 / 98765-3",
    salaryAmount: 9100,
  },
  "44444444-4444-4444-8444-444444444444": {
    cpf: "456.789.012-33",
    bankAccount: "1234-5 / 98765-4",
    salaryAmount: 6400,
  },
};

const PAGE_SIZE = 25;

function normalize(value: string): string {
  return value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

/** Diretório mock tipado para VITE_AUTH_MODE=demo. */
export function listDemoEmployees(query?: {
  cursor?: string;
  limit?: number;
  status?: "ACTIVE" | "ON_LEAVE" | "TERMINATED" | "UNKNOWN";
  sort?: "fullName:asc" | "fullName:desc" | "hireDate:asc" | "hireDate:desc";
  q?: string;
}): EmployeeListResponse {
  const limit = query?.limit ?? PAGE_SIZE;
  let rows = [...DEMO];
  if (query?.status) {
    rows = rows.filter((r) => r.status === query.status);
  }
  if (query?.q) {
    const qNorm = normalize(query.q);
    rows = rows.filter((r) =>
      normalize(
        [r.fullName, r.email ?? "", r.registrationNumber ?? "", r.departmentName].join(
          " ",
        ),
      ).includes(qNorm),
    );
  }
  const sort = query?.sort ?? "fullName:asc";
  const [field, direction] = sort.split(":") as ["fullName" | "hireDate", "asc" | "desc"];
  rows.sort((a, b) => {
    const left = (field === "hireDate" ? a.hireDate : a.fullName) ?? "";
    const right = (field === "hireDate" ? b.hireDate : b.fullName) ?? "";
    const cmp = String(left).localeCompare(String(right), "pt-BR");
    return direction === "asc" ? cmp : -cmp;
  });
  const start = query?.cursor ? Number.parseInt(query.cursor, 10) : 0;
  const offset = Number.isFinite(start) && start >= 0 ? start : 0;
  const slice = rows.slice(offset, offset + limit);
  const next = offset + limit;
  const hasMore = next < rows.length;
  return {
    data: slice,
    pageInfo: {
      hasMore,
      nextCursor: hasMore ? String(next) : null,
    },
  };
}

export function getDemoEmployeeDetail(id: string): EmployeeDetail {
  const base = DEMO.find((e) => e.id === id);
  if (!base) throw new Error("Colaborador não encontrado (demo)");
  const platform = platformOf(base);
  return {
    ...base,
    displayName: platform.displayName,
    internalNotes: platform.internalNotes,
    version: platform.version,
    emailEditable: platform.emailEditable,
    fieldHistory: [...platform.history].reverse(),
    fieldOrigins: [
      { field: "fullName", ownership: "ERP" },
      { field: "registrationNumber", ownership: "ERP" },
      { field: "cpf", ownership: "ERP" },
      { field: "status", ownership: "ERP" },
      { field: "hireDate", ownership: "ERP" },
      { field: "departmentName", ownership: "ERP" },
      { field: "positionName", ownership: "ERP" },
      { field: "salaryAmount", ownership: "ERP" },
      { field: "bankAccount", ownership: "ERP" },
      { field: "email", ownership: "HYBRID" },
      { field: "displayName", ownership: "PLATFORM" },
      { field: "internalNotes", ownership: "PLATFORM" },
      { field: "syncedAt", ownership: "PLATFORM" },
    ],
    vacationPeriods: [
      { label: "Período aquisitivo vigente", balanceDays: 18, expiresAt: "2027-01-15" },
    ],
    payslips: [
      {
        competence: "2026-07",
        netAmount: 4820.55,
        availableAt: "2026-08-05T12:00:00.000Z",
      },
    ],
    benefits: [
      { name: "Plano de saúde", status: "ACTIVE" },
      { name: "Vale-refeição", status: "ACTIVE" },
    ],
    documents: [
      {
        name: "Contrato de trabalho",
        category: "ADMISSION",
        issuedAt: base.hireDate ?? null,
      },
    ],
  };
}

export function patchDemoEmployee(id: string, body: PatchEmployeeBody): EmployeeDetail {
  const base = DEMO.find((e) => e.id === id);
  if (!base) throw new Error("Colaborador não encontrado (demo)");
  const platform = platformOf(base);
  if (body.expectedVersion !== platform.version) {
    throw new Error("VERSION_CONFLICT");
  }
  const auditId = crypto.randomUUID();
  const at = new Date().toISOString();
  if (body.displayName !== undefined && body.displayName !== platform.displayName) {
    platform.history.push({
      id: crypto.randomUUID(),
      field: "displayName",
      previous: platform.displayName,
      next: body.displayName,
      at,
      actorUserId: "demo-hr",
      auditId,
    });
    platform.displayName = body.displayName;
  }
  if (body.internalNotes !== undefined && body.internalNotes !== platform.internalNotes) {
    platform.history.push({
      id: crypto.randomUUID(),
      field: "internalNotes",
      previous: platform.internalNotes,
      next: body.internalNotes,
      at,
      actorUserId: "demo-hr",
      auditId,
    });
    platform.internalNotes = body.internalNotes;
  }
  platform.version += 1;
  return getDemoEmployeeDetail(id);
}

export function revealDemoEmployeeField(
  id: string,
  field: RevealEmployeeField,
): RevealEmployeeFieldResult {
  const secrets = SECRETS[id];
  if (!secrets) throw new Error("Colaborador não encontrado (demo)");
  return {
    field,
    value: String(secrets[field]),
    auditId: crypto.randomUUID(),
  };
}

/** Export demo — simula CSV + auditId local. */
export function exportDemoEmployees(query?: {
  status?: "ACTIVE" | "ON_LEAVE" | "TERMINATED" | "UNKNOWN";
  sort?: "fullName:asc" | "fullName:desc" | "hireDate:asc" | "hireDate:desc";
  q?: string;
}): { csv: string; filename: string; auditId: string; rowCount: number } {
  const page = listDemoEmployees({ ...query, limit: 5000 });
  const headers = [
    "id",
    "fullName",
    "status",
    "email",
    "departmentName",
    "positionName",
    "hireDate",
    "syncedAt",
    "dataOrigin",
  ];
  const escape = (cell: string) => `"${cell.replaceAll('"', '""')}"`;
  const lines = [
    headers.map(escape).join(","),
    ...page.data.map((row) =>
      [
        row.id,
        row.fullName,
        row.status,
        row.email ?? "",
        row.departmentName,
        row.positionName ?? "",
        row.hireDate ?? "",
        row.syncedAt ?? "",
        row.dataOrigin ?? "",
      ]
        .map(escape)
        .join(","),
    ),
  ];
  return {
    csv: `\uFEFF${lines.join("\n")}`,
    filename: `colaboradores-demo.csv`,
    auditId: crypto.randomUUID(),
    rowCount: page.data.length,
  };
}
